import {
  Chart as ChartJS,
  Plugin,
  Chart,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Tooltip,
  Legend,
} from 'chart.js';
import React, { useEffect, useState } from 'react';
import { Line } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';

import currentTimeZone from '../../../../config/timeZone';
import { useAppSelector } from '../../../../redux/hooks';
import BatteryLoader from '../../../../shared-components/battery-loader/BatteryLoader';
import {
  fillMissingLast24HrsGeneric,
  fillMissingLastNDaysGenericLatest,
  fillMissingMonthsGeneric,
} from '../../../../shared-functions/ChartFunctions';
import ToFixedValue from '../../../../shared-functions/ToFixedValue';
import endConsumerApi from '../../../../utils/api/end-consumer-v2';

interface TotalSessionsResponse {
  totalActiveConsumers: number;
  totalPowerConsumedInKwh: number;
  totalRevenue: number;
  totalSessions: number;
}

/* ------------------ ChartJS Register ------------------ */
ChartJS.register(LineElement, PointElement, LinearScale, CategoryScale, Tooltip, Legend);

/* ------------------ Hover Center Line Plugin ------------------ */
const verticalHoverLinePlugin: Plugin<'line'> = {
  id: 'verticalHoverLine',

  /**
   * afterDatasetsDraw
   * @param chart
   * @returns
   */
  afterDatasetsDraw: (chart: Chart) => {
    const { tooltip } = chart;

    if (!tooltip || !tooltip.getActiveElements().length) return;

    const { ctx } = chart;
    const activePoint = tooltip.getActiveElements()[0];
    const { x } = activePoint.element;

    const topY = chart.scales.y.top;
    const bottomY = chart.scales.y.bottom;

    const successDark = getComputedStyle(document.documentElement)
      .getPropertyValue('--success')
      .trim();

    ctx.save();

    ctx.beginPath();
    ctx.lineWidth = 1;
    ctx.strokeStyle = successDark;
    ctx.lineCap = 'butt'; // straight line (no caps)
    ctx.moveTo(x, topY); // ✅ from top
    ctx.lineTo(x, bottomY); // ✅ to bottom
    ctx.stroke();

    ctx.restore();
  },
};

interface ChartDataProperties {
  label: string;
  [key: string]: string;
}

export type TimeRange = 'last24hours' | 'last7days' | 'lastMonth' | 'last12months';

/**
 * Component to display revenue vs power consumed chart
 * @returns
 */
export default function AfterDatasetsDrawChart() {
  const { t, i18n } = useTranslation('endConsumer');
  const [powerConsumedData, setPowerConsumedData] = useState<ChartDataProperties[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const { endConsumerType, reportFilter } = useAppSelector((state) => state.endConsumerType);
  const [chartValues, setChartValues] = useState<TotalSessionsResponse | null>(null);

  const activeRangeReference = React.useRef<
    'last24hours' | 'last7days' | 'lastMonth' | 'last12months'
  >('last24hours');

  const chartData = {
    labels: powerConsumedData.map((d) => d.label),
    datasets: [
      {
        label: t('totalSessions'),
        data: powerConsumedData.map((d) => d.totalSessions),
        borderColor: '#2AA700',
        /**
         * Background
         * @param context
         * @returns
         */
        backgroundColor: (context: any) => {
          const { chart } = context;
          const contentContext = chart.ctx;
          const gradient = contentContext.createLinearGradient(0, 0, 0, chart.height);
          gradient.addColorStop(0, 'rgba(247, 144, 9, 0)'); // Alpha 0.4
          gradient.addColorStop(1, 'rgba(237, 237, 237, 0)');
          return gradient;
        },
        fill: 'origin', // Fills to the previous dataset
        tension: 0.4,
        borderWidth: 1,
        pointRadius: 0,
        pointHoverRadius: 4,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
        position: 'top' as const, // Explicitly cast to 'top' to ensure correct type
        align: 'end' as const,
        labels: {
          usePointStyle: true,
          boxWidth: 10,
          boxHeight: 10,
          borderRadius: 10,
          padding: 15,
        },
        padding: {
          right: 500, // Add 50px padding to the right side of the legend
        },
      },
      tooltip: {
        backgroundColor: '#1F2937',
        titleColor: '#FFFFFF',
        bodyColor: '#FFFFFF',
        padding: 10,
        borderRadius: 8,
        cornerRadius: 4,
        displayColors: false,
        callbacks: {
          /**
           * Consumption
           * @returns
           */
          title: () => t('sessions'),
          /**
           * Consumption values
           * @returns
           */
          label: (context: any) => {
            return `${context.raw} `;
          },
        },
      },
    },

    interaction: {
      mode: 'index' as const,
      intersect: false,
    },

    scales: {
      x: {
        grid: {
          display: false,
        },
        border: {
          display: false, // Hides the y-axis line
        },
      },
      y: {
        grid: {
          display: false,
        },
        ticks: {
          display: true,
        },
        border: {
          display: false,
        },
      },
    },
  };

  /**
   * Fetch power consumed data based on time range
   * @param range
   */
  const fetchPowerConsumedReport = async (
    range: 'last24hours' | 'last7days' | 'lastMonth' | 'last12months',
  ) => {
    activeRangeReference.current = range;
    setPowerConsumedData([]);
    try {
      setIsLoading(true);
      const response = await endConsumerApi.fetchTotalChartApi({
        timePeriod: range,
        timeZone: currentTimeZone,
        type: endConsumerType,
      });

      // ✅ Otherwise, update the chart with the right data
      const result = response.data?.results;
      if (range === 'last12months' && response.status === 200) {
        setPowerConsumedData(fillMissingMonthsGeneric(result));
      } else if (range === 'lastMonth' && response.status === 200) {
        setPowerConsumedData(fillMissingLastNDaysGenericLatest(result, 30));
      } else if (range === 'last7days' && response.status === 200) {
        setPowerConsumedData(fillMissingLastNDaysGenericLatest(result, 7));
      } else if (range === 'last24hours' && response.status === 200) {
        setPowerConsumedData(fillMissingLast24HrsGeneric(result));
      }
      setChartValues(response?.data);
      setIsLoading(false);
    } catch (error) {
      console.error(error);

      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (reportFilter.range) {
      fetchPowerConsumedReport(reportFilter.range as TimeRange);
    }
  }, [reportFilter.range, endConsumerType]);

  return (
    <div className="total-revenue-chart-wrap border" dir={i18n.dir()}>
      <div className="total-revenue-chart-header">
        <p className="header-title">{t('totalSessions')}</p>
      </div>

      {isLoading ? (
        <div className="revenue-consumer-loader">
          <BatteryLoader />
        </div>
      ) : (
        <>
          <div className="total-revenue-chart-container">
            <div className="chart-wrapper">
              <Line data={chartData} options={options} plugins={[verticalHoverLinePlugin]} />
            </div>
          </div>
          <div className="revenue-footer">
            <div className="color-border" style={{ background: '#2AA700' }} />
            <p className="title">{t('sessionCount')}</p>
            <p className="value">{ToFixedValue(chartValues?.totalSessions)}</p>
          </div>
        </>
      )}
    </div>
  );
}
